"use client";

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { useStreamdownTranslations } from "@/hooks/useStreamdownTranslations";
import { useTranslation } from "@/hooks/useTranslation";
import { cn } from "@/lib/utils";
import { useControllableState } from "@radix-ui/react-use-controllable-state";
import { BrainIcon, ChevronDownIcon } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import type { ComponentProps, ReactNode } from "react";
import { createContext, memo, useContext, useEffect, useState } from "react";
import { Streamdown } from "streamdown";
import { Shimmer } from "./shimmer";

type ReasoningContextValue = {
  isStreaming: boolean;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  duration: number | undefined;
};

const ReasoningContext = createContext<ReasoningContextValue | null>(null);

function useReasoning() {
  const context = useContext(ReasoningContext);
  if (!context) {
    throw new Error("Reasoning components must be used within Reasoning");
  }
  return context;
}

export type ReasoningProps = ComponentProps<typeof Collapsible> & {
  isStreaming?: boolean;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  duration?: number;
};

const MS_IN_S = 1000;

export const Reasoning = memo(
  ({
    className,
    isStreaming = false,
    open,
    // Closed unless the reader opens it: the pill says what the model is on,
    // and a panel unfolding over the answer then folding away on its own made
    // the whole message jump twice per reply.
    defaultOpen = false,
    onOpenChange,
    duration: durationProp,
    children,
    ...props
  }: ReasoningProps) => {
    const [isOpen, setIsOpen] = useControllableState({
      prop: open,
      defaultProp: defaultOpen,
      onChange: onOpenChange,
    });
    const [duration, setDuration] = useControllableState({
      prop: durationProp,
      defaultProp: undefined,
    });

    const [startTime, setStartTime] = useState<number | null>(null);

    // Track duration when streaming starts and ends
    useEffect(() => {
      if (isStreaming) {
        if (startTime === null) {
          setStartTime(Date.now());
        }
      } else if (startTime !== null) {
        setDuration(Math.ceil((Date.now() - startTime) / MS_IN_S));
        setStartTime(null);
      }
    }, [isStreaming, startTime, setDuration]);

    const handleOpenChange = (newOpen: boolean) => {
      setIsOpen(newOpen);
    };

    return (
      <ReasoningContext.Provider
        value={{ isStreaming, isOpen, setIsOpen, duration }}
      >
        <Collapsible
          data-slot="reasoning"
          className={cn("mb-4 not-prose", className)}
          onOpenChange={handleOpenChange}
          open={isOpen}
          {...props}
        >
          {children}
        </Collapsible>
      </ReasoningContext.Provider>
    );
  }
);

export type ReasoningTriggerProps = ComponentProps<
  typeof CollapsibleTrigger
> & {
  getThinkingMessage?: (isStreaming: boolean, duration?: number) => ReactNode;
  /**
   * What the model is thinking about right now — the title of the reasoning
   * section being written. Shown, shimmering, while it streams; falls back on
   * the generic "thinking" label while empty.
   */
  liveLabel?: string;
};

/**
 * The label of a reasoning still streaming: the shimmer wave on the text, and
 * each new title sliding in over the last one, so a closed pill still shows the
 * model moving on from one step to the next.
 */
export function ReasoningLiveLabel({ label }: { label: string }) {
  return (
    <span className="relative inline-flex min-w-0 overflow-hidden">
      <AnimatePresence initial={false} mode="popLayout">
        <motion.span
          key={label}
          className="block min-w-0 truncate"
          initial={{ y: "100%", opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: "-100%", opacity: 0 }}
          transition={{ duration: 0.25, ease: "easeOut" }}
        >
          <Shimmer as="span" className="max-w-full truncate align-bottom">
            {label}
          </Shimmer>
        </motion.span>
      </AnimatePresence>
    </span>
  );
}

export const ReasoningTrigger = memo(
  ({
    className,
    children,
    getThinkingMessage,
    liveLabel,
    ...props
  }: ReasoningTriggerProps) => {
    const { isStreaming, isOpen, duration } = useReasoning();
    const { t } = useTranslation();

    const defaultGetThinkingMessage = (
      streaming: boolean,
      dur?: number
    ): ReactNode => {
      if (streaming || dur === 0) {
        return (
          <ReasoningLiveLabel label={liveLabel || t("chat.reasoning.thinking")} />
        );
      }
      if (dur === undefined) {
        return <span>{t("chat.reasoning.thoughtForFewSeconds")}</span>;
      }
      return (
        <span>{t("chat.reasoning.thoughtForSeconds", { count: dur })}</span>
      );
    };

    const getMessage = getThinkingMessage ?? defaultGetThinkingMessage;

    return (
      <CollapsibleTrigger
        data-slot="reasoning-trigger"
        className={cn(
          "inline-flex gap-2 items-center w-fit max-w-full rounded-full border px-3 py-1.5 text-sm transition-colors text-muted-foreground hover:bg-muted",
          className
        )}
        {...props}
      >
        {children ?? (
          <>
            <BrainIcon className="size-4 shrink-0 text-majorelle" />
            {getMessage(isStreaming, duration)}
            <ChevronDownIcon
              className={cn(
                "transition-transform size-4 shrink-0",
                isOpen ? "rotate-180" : "rotate-0"
              )}
            />
          </>
        )}
      </CollapsibleTrigger>
    );
  }
);

export type ReasoningContentProps = ComponentProps<
  typeof CollapsibleContent
> & {
  children: string;
};

export const ReasoningContent = memo(
  ({ className, children, ...props }: ReasoningContentProps) => {
    const translations = useStreamdownTranslations();

    return (
      <CollapsibleContent
        data-slot="reasoning-content"
        className={cn(
          "mt-3 ml-1 border-l-2 border-border pl-3.5 text-sm",
          "data-[state=closed]:fade-out-0 data-[state=closed]:slide-out-to-top-2 data-[state=open]:slide-in-from-top-2 text-muted-foreground outline-hidden data-[state=closed]:animate-out data-[state=open]:animate-in",
          className
        )}
        {...props}
      >
        <Streamdown translations={translations}>{children}</Streamdown>
      </CollapsibleContent>
    );
  }
);

Reasoning.displayName = "Reasoning";
ReasoningTrigger.displayName = "ReasoningTrigger";
ReasoningContent.displayName = "ReasoningContent";
